import { apiClient } from "@/lib/api-client"
import {
  WorkflowInstanceDto,
  LiveWorkflowsFilter,
  LiveWorkflowsResponse,
} from "../workflows/dtos/liveWorkflowDto"

interface BackendComment {
  id: string
  text: string
  commentType: 0 | 1 | 2
  authorEmail?: string
  createdAt: string
}

interface BackendTaskState {
  id: string
  workflowTaskId: string
  title: string
  description?: string
  assignedToEmail: string
  assigneeType: number
  status: number | string
  duedate?: string
  completedAt?: string
  declinedAt?: string
  comments?: BackendComment[]
  createdAt: string
  updatedAt: string
}

interface BackendWorkflowState {
  id: string
  workflowId: string
  workflowName: string
  contextDescription?: string
  employeeName?: string
  status: number | string
  startedAt: string
  completedAt?: string
  cancelledAt?: string
  batchId?: string
  batchName?: string
  referenceNumber?: string
  tasks?: BackendTaskState[]
}

class LiveWorkflowService {
  async getLiveWorkflows(
    orgId: string,
    filter: LiveWorkflowsFilter = {},
  ): Promise<LiveWorkflowsResponse> {
    const { data } = await apiClient.get<BackendWorkflowState[]>(
      `/organizations/${orgId}/workflow-states`,
      {
        params: {
          includeTasks: true,
          ...filter,
        },
      },
    )

    // Transform backend WorkflowStateDto to frontend WorkflowInstanceDto
    const items: WorkflowInstanceDto[] = data.map(
      (state: BackendWorkflowState) => ({
        id: state.id,
        workflowId: state.workflowId,
        workflowName: state.workflowName,
        workflowDescription: state.contextDescription,
        assignedToName: state.employeeName || "Standalone",
        assignedToEmail: state.employeeName ? "" : "N/A",
        organizationId: orgId,
        organizationName: "",
        status: this.mapStatus(state.status),
        startedAt: state.startedAt,
        completedAt: state.completedAt,
        cancelledAt: state.cancelledAt,
        batchId: state.batchId,
        batchName: state.batchName,
        referenceNumber: state.referenceNumber,
        currentTaskIndex:
          state.tasks?.filter(
            (t: BackendTaskState) => t.status === 1 || t.status === 2,
          ).length || 0,
        totalTasks: state.tasks?.length || 0,
        progressPercentage: state.tasks?.length
          ? Math.round(
              (state.tasks.filter((t: BackendTaskState) => t.status === 1)
                .length /
                state.tasks.length) *
                100,
            )
          : 0,
        tasks:
          state.tasks?.map((task: BackendTaskState) => ({
            id: task.id,
            workflowInstanceId: state.id,
            workflowTaskId: task.workflowTaskId,
            title: task.title,
            description: task.description,
            assignedToName: "",
            assignedToEmail: task.assignedToEmail,
            assigneeType: task.assigneeType === 0 ? "Internal" : "External",
            status: this.mapTaskStatus(task.status),
            dueDate: task.duedate || "",
            completedAt: task.completedAt,
            declinedAt: task.declinedAt,
            comments: task.comments ?? [],
            orderIndex: 0,
            createdAt: task.createdAt,
            updatedAt: task.updatedAt,
          })) || [],
        createdAt: state.startedAt,
        updatedAt: state.completedAt || state.startedAt,
      }),
    )

    return {
      items,
      totalCount: items.length,
      currentPage: 1,
      pageSize: items.length,
      totalPages: 1,
    }
  }

  private mapStatus(
    backendStatus: number | string,
  ): WorkflowInstanceDto["status"] {
    // Backend sends string enum names, not numbers
    if (typeof backendStatus === "string") {
      switch (backendStatus) {
        case "InProgress":
          return "InProgress"
        case "Completed":
          return "Completed"
        case "Cancelled":
          return "Cancelled"
        default:
          return "InProgress"
      }
    }
    // Fallback for numeric values (legacy)
    switch (backendStatus) {
      case 0:
        return "InProgress"
      case 1:
        return "Completed"
      case 2:
        return "Cancelled"
      default:
        return "InProgress"
    }
  }

  private mapTaskStatus(
    backendStatus: number | string,
  ): "Pending" | "InProgress" | "Completed" | "Declined" | "Expired" {
    // Backend sends string enum names, not numbers
    if (typeof backendStatus === "string") {
      switch (backendStatus) {
        case "Pending":
          return "Pending"
        case "Completed":
          return "Completed"
        case "Declined":
          return "Declined"
        case "Expired":
        case "Skipped":
          return "Expired"
        default:
          return "Pending"
      }
    }
    // Fallback for numeric values (legacy)
    switch (backendStatus) {
      case 0:
        return "Pending"
      case 1:
        return "Completed"
      case 2:
        return "Declined"
      case 3:
      case 4:
        return "Expired"
      default:
        return "Pending"
    }
  }

  async getWorkflowInstance(
    orgId: string,
    instanceId: string,
  ): Promise<WorkflowInstanceDto> {
    const { data } = await apiClient.get<WorkflowInstanceDto>(
      `/organizations/${orgId}/workflow-states/${instanceId}`,
    )
    return data
  }

  async cancelWorkflow(
    orgId: string,
    instanceId: string,
    reason?: string,
  ): Promise<void> {
    await apiClient.post(
      `/organizations/${orgId}/workflow-states/${instanceId}/cancel`,
      { reason },
    )
  }

  async reactivateWorkflow(orgId: string, instanceId: string): Promise<void> {
    await apiClient.post(
      `/organizations/${orgId}/workflow-states/${instanceId}/reactivate`,
      {},
    )
  }

  async cancelTask(
    orgId: string,
    instanceId: string,
    taskId: string,
    reason?: string,
  ): Promise<void> {
    await apiClient.post(
      `/organizations/${orgId}/workflow-states/${instanceId}/tasks/${taskId}/cancel`,
      { reason },
    )
  }

  async reactivateTask(
    orgId: string,
    instanceId: string,
    taskId: string,
  ): Promise<void> {
    await apiClient.post(
      `/organizations/${orgId}/workflow-states/${instanceId}/tasks/${taskId}/reactivate`,
      {},
    )
  }

  async getWorkflowInstanceTasks(
    orgId: string,
    instanceId: string,
  ): Promise<WorkflowInstanceDto["tasks"]> {
    const instance = await this.getWorkflowInstance(orgId, instanceId)
    return instance.tasks
  }

  async deleteWorkflowInstance(
    orgId: string,
    instanceId: string,
  ): Promise<void> {
    await apiClient.delete(
      `/organizations/${orgId}/workflow-states/${instanceId}`,
    )
  }
}

export const liveWorkflowService = new LiveWorkflowService()
